/**
 * Axis-domain maths for the segment-analysis charts (bubble chart, §defect #4).
 *
 * The bubble chart used to clamp its x/y scales at fixed constants (addressable
 * market at 1300, growth at 13%). Real data rarely spans the full clamp range,
 * so segments bunched into a corner and most of the chart stayed blank. These
 * helpers fit an axis to the actual data range instead, with padding and
 * "nice" round tick values, and cover the cases a fixed clamp got wrong: no
 * data, a single data point, and negative values (e.g. negative CAGR).
 */

export type ChartAxisDomain = {
  max: number
  min: number
  ticks: number[]
}

/** Rounds a raw tick step up to a "nice" 1 / 2 / 5 × 10^n number (Heckbert's algorithm). */
function niceStep(rawStep: number): number {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 1

  const exponent = Math.floor(Math.log10(rawStep))
  const magnitude = 10 ** exponent
  const fraction = rawStep / magnitude

  if (fraction <= 1) return magnitude
  if (fraction <= 2) return 2 * magnitude
  if (fraction <= 5) return 5 * magnitude
  return 10 * magnitude
}

/** Evenly spaced "nice" tick values covering at least [min, max]. */
export function niceTicks(min: number, max: number, targetCount = 5): number[] {
  if (min === max) return [min]

  const step = niceStep((max - min) / Math.max(1, targetCount - 1))
  const niceMin = Math.floor(min / step) * step
  const niceMax = Math.ceil(max / step) * step
  const ticks: number[] = []

  for (let value = niceMin; value <= niceMax + step / 1e6; value += step) {
    // Snap to the step grid to kill float noise (e.g. 0.30000000000000004)
    // without losing precision the step itself doesn't have.
    ticks.push(Math.round(value / step) * step)
  }

  return ticks
}

/**
 * Fits an axis domain to `values`, padded and snapped to nice tick boundaries.
 * Handles the degenerate cases a hard-coded clamp got wrong:
 * - no values (0 segments) -> a sensible default range instead of NaN
 * - one value, or every value equal (1 segment) -> a range centered on it,
 *   instead of a zero-width domain that divides by zero downstream
 * - negative values (negative growth) -> the domain extends below zero
 *   instead of clamping there
 * - `minZero` (quantities that cannot be negative, e.g. addressable market)
 *   -> padding never pushes the domain below 0, and all-zero data gets
 *   [0, fallbackSpan] instead of a range centred on 0
 */
export function fitAxisDomain(
  values: number[],
  options: {
    fallbackSpan?: number
    minZero?: boolean
    padFraction?: number
    tickCount?: number
  } = {}
): ChartAxisDomain {
  const { fallbackSpan = 10, minZero = false, padFraction = 0.12, tickCount = 5 } = options
  const finite = values.filter((value) => Number.isFinite(value))

  if (finite.length === 0) {
    const ticks = niceTicks(0, fallbackSpan, tickCount)
    return { max: ticks[ticks.length - 1], min: ticks[0], ticks }
  }

  let dataMin = Math.min(...finite)
  let dataMax = Math.max(...finite)

  // Only clamp when the data itself is non-negative; a stray negative value
  // must stay on the chart rather than be clipped off it.
  const clampAtZero = minZero && dataMin >= 0

  if (dataMin === dataMax) {
    if (clampAtZero && dataMax === 0) {
      const ticks = niceTicks(0, fallbackSpan, tickCount)
      return { max: ticks[ticks.length - 1], min: ticks[0], ticks }
    }
    const span = dataMax !== 0 ? Math.abs(dataMax) : fallbackSpan
    dataMin -= span / 2
    dataMax += span / 2
  }

  const pad = (dataMax - dataMin) * padFraction
  const lower = clampAtZero ? Math.max(0, dataMin - pad) : dataMin - pad
  const ticks = niceTicks(lower, dataMax + pad, tickCount)

  return { max: ticks[ticks.length - 1], min: ticks[0], ticks }
}

/** Formats a nice-tick value for an axis label: kills float noise, keeps at most one decimal. */
export function formatAxisTick(value: number): string {
  const rounded = Math.round(value * 10) / 10
  return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(1)
}
